// Shared post-DDL metadata-cache invalidation extracted from the query-editor
// path (INT-15) so the Table-Designer DB-apply path invalidates identically and
// the two can't drift. Preserves the query path's exact gating and fire-and-
// forget index call. Callers must gate on a non-empty connectionId before
// calling — the helper assumes a valid id and never short-circuits itself.
import type { DatabaseEngineId } from '@/core/engines';
import { DATABASE_ENGINE_IDS } from '@/core/engines';

import { isDatabaseDdl } from './databaseDdlDetector';
import { isRoutineDdl, isSchemaDdl, isTableDdl } from './ddlDetector';
import { type MaskOptions, mysqlMaskOptionsForConnection } from './sqlTextMasker';
import { invalidateColumns } from './insertHintColumnResolver';
import { invalidateRoutines } from './routineParameterResolver';
import { dropMssqlColumnCache } from '../../../intellisense/resolvers/mssqlColumnCache';
import { dropMssqlTables } from '../../../connections/services/language-services/mssqlTableCache';
import {
  dropMysqlSchema,
  getCachedMysqlSchema,
} from '../../../connections/services/language-services/mysqlSchemaCache';
import { dropOracleSchema } from '../../../connections/services/language-services/oracleSchemaCache';
import { dropPgSchema } from '../../../connections/services/language-services/pgSchemaCache';
import { dropSqliteSchema } from '../../../connections/services/language-services/sqliteSchemaCache';
import { invalidateSchema as invalidateSchemaBus } from '../../../intellisense/schemaBus/schemaBus';
import { runIndexInvalidationForDdl } from '../../../navigational-features/index/indexInvalidator';
import { notifyDdl as notifyResolverDdl } from '../../../sql-resolver/service/ddlNotifier';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { invalidateDatabaseListing } from '../../../database-objects/hooks/useDatabaseQueries';

/**
 * The masking context for `isSchemaDdl` — its string/comment masker reads `[`,
 * `"`, `` ` ``, `$…$` and `q'…'` per the engine's grammar
 * (`sqlTextMasker.ts`: PostgreSQL `[` is an array constructor, MySQL `"` is a
 * string literal), so masking without it can let a value inside
 * `ARRAY['DROP VIEW x']` read as DDL.
 *
 * Resolved from the connection store rather than threaded through
 * `invalidateAfterDdl`'s parameters: neither production caller (the query
 * editor's post-success path, the Table Designer's DB-apply path) carries the
 * engine. `getConnectionById` is the shared lookup (saved profile first, then
 * the active connection) and defaults an unknown / engine-less profile to
 * `'mssql'` — the SAME default the navigational lifecycle registers indexes
 * with (`connection-lifecycle/navLifecycleAttach.ts`, `engine ?? 'mssql'`), so this masker and
 * the index classifier's never disagree about a pre-multi-engine saved profile
 * where `Connection.engine` is absent.
 *
 * MySQL's `"…"` and its backslash escapes are what the SQL TEXT cannot decide:
 * under `ANSI_QUOTES` the first is an identifier rather than a string, under
 * `NO_BACKSLASH_ESCAPES` the second is an ordinary character. The session's
 * `sql_mode` IS observable — the MySQL IntelliSense snapshot carries it — so
 * both are read from there. A snapshot that has not been cached yet leaves
 * them `false`, i.e. the MySQL server defaults.
 *
 * READ ONCE, BEFORE ANY DROP: `invalidateAfterDdl` drops that very snapshot
 * (`dropMysqlSchema`) partway through, so anything that re-derives the options
 * afterwards reads a missing `sqlMode` and falls back to the server default.
 * The captured value is threaded on to `runIndexInvalidationForDdl` for
 * exactly that reason.
 *
 * That capture only settles the call that did the dropping. The NEXT DDL on the
 * same connection — a second `DROP VIEW "v2"` before anything rebuilt the
 * snapshot — finds nothing to read at all, which is why the mode is REMEMBERED
 * per connection (`mysqlMaskOptionsForConnection`, forgotten on teardown) and
 * the snapshot read below is only the newest source, not the only one.
 */
function maskOptionsForConnection(connectionId: string): MaskOptions {
  try {
    const { engine } = getConnectionById(connectionId);
    const engineId = (DATABASE_ENGINE_IDS as readonly string[]).includes(engine)
      ? (engine as DatabaseEngineId)
      : 'mssql';
    return engineId === 'mysql'
      ? mysqlMaskOptionsForConnection(connectionId, getCachedMysqlSchema(connectionId)?.sqlMode)
      : { engine: engineId };
  } catch {
    return { engine: 'mssql' };
  }
}

/**
 * Invalidate all metadata caches that may be stale after a DDL statement is
 * successfully applied. Mirrors the block formerly inline in
 * `useQueryExecution.ts`'s post-success path — extracted so both the
 * query-editor and Table-Designer apply paths call the exact same logic.
 *
 * The index invalidation call is fire-and-forget (`.catch(() => {})`) to match
 * the original timing: schema drift there must not propagate to the caller.
 *
 * Returns whether the SQL was schema-shape DDL, so a caller that has to wait
 * for the rebuilt snapshot (the E2E seam below) knows — and knows it from the
 * masking options captured BEFORE the drops rather than re-deriving them from
 * the caches this call has just emptied.
 */
export function invalidateAfterDdl(connectionId: string, sql: string): boolean {
  const didRoutineDdl = isRoutineDdl(sql);
  const didTableDdl = isTableDdl(sql);
  const mask = maskOptionsForConnection(connectionId);
  const didSchemaDdl = isSchemaDdl(sql, mask.engine, mask);
  if (didRoutineDdl) invalidateRoutines(connectionId);
  if (didTableDdl) {
    invalidateColumns(connectionId);
    // Drop the MSSQL per-table column cache — the bus snapshot carries
    // `columns: []` for MSSQL so this cache is the column source the MSSQL
    // native resolver serves to autocomplete. Without this, table DDL (e.g.
    // ADD COLUMN) leaves stale columns for the 60s TTL. Engine-agnostic:
    // no-ops when no MSSQL entries exist.
    dropMssqlColumnCache(connectionId);
  }
  if (didSchemaDdl) {
    // Drop the MSSQL native table/routine cache TOO — invalidateSchemaBus
    // clears the bus, but the resolver's fire-and-forget re-ensure consults
    // the native cache's 60s TTL first: a still-fresh entry is returned
    // WITHOUT re-publishing, leaving the bus empty (degraded completions)
    // until TTL expiry and serving pre-DDL routines/tables after. Dropping
    // the native entry makes the next ensure a real refresh + publish.
    // Engine-agnostic no-op when no MSSQL entries exist.
    dropMssqlTables(connectionId);
    // Same TTL trap exists for the Oracle/PG native caches (fresh entry
    // re-served without republish) — drop them too. Engine-agnostic no-ops
    // when the connection has no entry in that cache.
    dropOracleSchema(connectionId);
    dropPgSchema(connectionId);
    // MySQL/SQLite were missing from this list: their 30 s cache TTL happened
    // to recover the trap on its own within a minute. S7 item 3 raised every
    // native cache's TTL to a day-scale backstop (`snapshotLifetime.ts`), so
    // DDL invalidation is now the ONLY thing that rebuilds them — without
    // these two drops a CREATE/DROP would leave stale completions for hours.
    dropMysqlSchema(connectionId);
    dropSqliteSchema(connectionId);
    const objectKind = didTableDdl ? 'table' : didRoutineDdl ? 'routine' : 'schema';
    invalidateSchemaBus(connectionId, objectKind);
  }
  if (isDatabaseDdl(sql, mask.engine, mask)) {
    // The set of databases the connection can list just changed, so the ONE
    // cache slot every database picker reads (`useDatabaseList` →
    // `['database-objects','databases',connectionId]`) must not keep serving the
    // pre-DDL list. This is the post-EXECUTION hook — the query editor calls it
    // only after a statement succeeded, and the Table Designer only after an
    // apply — which is why the invalidation lives here and not next to the
    // visual DDL editor's `commitCreateDatabase` / `commitDropDatabase` (those
    // merely WRITE the statement into the buffer / stage it in the preview
    // dialog, so they fire even when the user then cancels).
    //
    // `mask.engine` is what makes `CREATE SCHEMA` count on MySQL (a database)
    // and not on PostgreSQL (a namespace) — see `isDatabaseDdl`.
    invalidateDatabaseListing(connectionId);
  }
  if (didRoutineDdl || didTableDdl) notifyResolverDdl(connectionId);
  if (didSchemaDdl) {
    // Fire-and-forget — the navigational-features index uses the same DDL
    // detector hook to refresh per-object reference records. Failures are
    // swallowed so schema drift here can't break the caller.
    //
    // `mask` is handed over rather than re-derived: `dropMysqlSchema` above
    // has already removed the snapshot this connection's `sql_mode` came from,
    // so the index invalidator's own derivation would read the server default
    // and mask a `DROP VIEW "v"` that ran under `ANSI_QUOTES` as a string —
    // leaving the dropped view indexed.
    runIndexInvalidationForDdl(connectionId, sql, mask).catch(() => {});
  }
  return didSchemaDdl;
}

// E2E seam — mirrors `__jamSqlSchemaBusPeek__` (schemaBus.ts): the coverage
// harness executes script runtime SQL via raw `executeQueryV2` IPC, which
// bypasses `useQueryExecution`'s post-success call to `invalidateAfterDdl`.
// This hook lets the harness run the SAME invalidation the app does, so
// mid-run DDL (CREATE PROCEDURE in script N) is visible to later scripts'
// completions. Returns whether the SQL was schema-shape DDL so the caller
// knows to await a snapshot refresh. Zero-cost in production: one window
// assignment at module load.
//
// The answer comes straight from `invalidateAfterDdl` — asking `isSchemaDdl`
// again AFTERWARDS re-derived the masking options from caches that call had
// just dropped, so a MySQL `ANSI_QUOTES` statement reported `false`.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__jamSqlInvalidateAfterDdl__ = (
    connectionId: string,
    sql: string,
  ): boolean => invalidateAfterDdl(connectionId, sql);
}
